/**
 * Command palette registry — context + hook for registering actions.
 *
 * Design: the `<CommandPalette />` UI reads from this context. Any component
 * can call `useRegisterCommands()` to publish actions to the palette; they
 * auto-unregister on unmount. This decouples the palette from any particular
 * page's data — the Reports page (R3) registers template-run actions; the
 * Dashboard (future) might register widget-focus actions; etc.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import type { LucideIcon } from 'lucide-react';

// ─── Types ──────────────────────────────────────────────────────────────────

export type CommandSectionId =
  | 'navigate'
  | 'reports'
  | 'actions'
  | 'recent';

export type CommandAction = {
  /** Stable unique ID. Re-registering with the same ID replaces the prior. */
  id: string;
  /** Display label in the palette. */
  label: string;
  /** Optional subdued description (e.g. template description, dept badge). */
  description?: string;
  /** Which section the action appears under. */
  section: CommandSectionId;
  /** Icon shown to the left of the label. */
  icon?: LucideIcon;
  /** Extra search keywords (boosts fuzzy match). */
  keywords?: string[];
  /** Human-readable shortcut hint shown at the right edge (e.g. "⌘R"). */
  shortcut?: string;
  /** Invoked when the user selects the action. */
  onSelect: () => void;
};

type PaletteContextValue = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  actions: CommandAction[];
  register: (actions: CommandAction[]) => () => void;
};

// ─── Context ────────────────────────────────────────────────────────────────

const PaletteContext = createContext<PaletteContextValue | null>(null);

// ─── Provider ───────────────────────────────────────────────────────────────

export function CommandPaletteProvider({
  children,
  seedActions = [],
}: {
  children: React.ReactNode;
  /** Static actions that always appear (e.g. navigation links). */
  seedActions?: CommandAction[];
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [dynamic, setDynamic] = useState<Map<string, CommandAction>>(new Map());

  const register = useCallback((newActions: CommandAction[]) => {
    if (newActions.length === 0) {
      // Nothing to register — skip the setState call entirely to avoid
      // re-render cascades for callers that pass an empty array.
      return () => {};
    }
    setDynamic((prev) => {
      const next = new Map(prev);
      for (const action of newActions) next.set(action.id, action);
      return next;
    });
    // Return cleanup so callers can unregister on unmount.
    return () => {
      setDynamic((prev) => {
        const next = new Map(prev);
        for (const action of newActions) next.delete(action.id);
        return next;
      });
    };
  }, []);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  // Global ⌘K / Ctrl+K listener.
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const isModK =
        (event.metaKey || event.ctrlKey) &&
        event.key.toLowerCase() === 'k' &&
        !event.shiftKey &&
        !event.altKey;
      if (isModK) {
        event.preventDefault();
        setIsOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const actions = useMemo(
    () => [...seedActions, ...dynamic.values()],
    [seedActions, dynamic],
  );

  const value = useMemo<PaletteContextValue>(
    () => ({ isOpen, open, close, toggle, actions, register }),
    [isOpen, open, close, toggle, actions, register],
  );

  return (
    <PaletteContext.Provider value={value}>{children}</PaletteContext.Provider>
  );
}

// ─── Hooks ──────────────────────────────────────────────────────────────────

export function useCommandPalette() {
  const ctx = useContext(PaletteContext);
  if (!ctx) {
    throw new Error('useCommandPalette must be used inside CommandPaletteProvider');
  }
  return ctx;
}

/**
 * Register a set of commands for the lifetime of the calling component.
 * Re-registers whenever `actions` identity changes — wrap in `useMemo` for
 * stable references.
 */
export function useRegisterCommands(actions: CommandAction[]) {
  const { register } = useCommandPalette();
  useEffect(() => {
    const unregister = register(actions);
    return unregister;
  }, [register, actions]);
}
